import { Component, OnInit } from '@angular/core';
import { DisplayOptions, ISyncAsOfDateInf } from '../../../shared/interfaces/display-options';
import { ReusableFunctions } from '../../../shared/classes/reusable-functions';
import { KPIService } from '../../../shared/services/kpi.service';
import { IKPIDataParams, IKPISparklineData, KPI } from '../../../shared/interfaces/kpi-data';
import { Router } from '@angular/router';
import { NoDataActionType } from '../../../shared/interfaces/no-data';
import { IPageHeadline } from '../../../shared/classes/page-title-parameters';
import { FiltersService } from '../../../shared/services/filters.service';
import { IStaffPosition } from '../../../shared/interfaces/user-location';
import {
    IChartParameters,
    SparklineChartOptions,
} from '../../../shared/classes/sparkline-chart-options';
import { ITableColumns } from '../../../reports/interfaces/reporting-data';
import {
    ICellClickEventInfo,
    ICustomTableInfoExtend,
} from 'app/modules/shared/components/custom-mat-table/custom-mat-table.component';
import { ReportingService } from 'app/modules/reports/services/reporting.service';

@Component({
    selector: 'app-performance-insights-kpi-sparkline',
    templateUrl: './performance-insights-kpi-sparkline.component.html',
    styleUrls: ['./performance-insights-kpi-sparkline.component.scss'],
})
export class PerformanceInsightsKPISparklineComponent implements OnInit {
    KPIs: KPI[];
    selectedKPI: KPI[];
    displayOptions: DisplayOptions[];
    hFilterJSON: string;
    spco: SparklineChartOptions;
    pageHeadline: IPageHeadline;
    roleName: string;
    hierarchyName: string;
    kpiListSettings: any;
    syncAsOfDateInf: ISyncAsOfDateInf;
    positions: IStaffPosition[];
    selectedPosition: IStaffPosition[];
    kpiProfileSettings: any;
    positionAbbv: string;
    parameters: IKPIDataParams;
    currentPeriodData: IKPISparklineData[];
    fullDataset: IKPISparklineData[];
    paginatedData: IKPISparklineData[];
    action: NoDataActionType;
    hierarchyIdentifier: string;

    columnInfoList: ITableColumns[];
    customTableInfo: ICustomTableInfoExtend;
    searchTerm: string;

    constructor(
        private _KPIService: KPIService,
        private _router: Router,
        private reusableFunctions: ReusableFunctions,
        private _filterService: FiltersService,
        private _reportService: ReportingService
    ) {}

    ngOnInit() {
        this.selectedPosition = [];
        this.paginatedData = [];
        this.pageHeadline = {
            module: 'performance',
            moduleIcon: 'fa-heartbeat',
            pageTitle: 'Employees Ranked per KPI',
            linkArray: [
                {
                    moduleLink: 'performance/insights',
                    linkTitle: 'performance insights',
                    showLink: true,
                },
            ],
        };
        this.searchTerm = '';

        this.kpiListSettings = {
            singleSelection: true,
            text: 'Select KPI',
            enableSearchFilter: true,
            showCheckbox: false,
            labelKey: 'clientKPIMatrixDescription',
            primaryKey: 'kpiMatrixKey',
            enableFilterSelectAll: false,
        };

        this.kpiProfileSettings = {
            singleSelection: true,
            text: 'Select KPI profile',
            enableSearchFilter: false,
            showCheckbox: false,
            labelKey: 'position',
            primaryKey: 'positionAbbv',
            enableFilterSelectAll: false,
        };

        this.currentPeriodData = [];
        this.spco = new SparklineChartOptions(this.reusableFunctions);
        this.hFilterJSON = this.reusableFunctions.getHierarchyFilters();
        this.displayOptions = this.reusableFunctions.getCurrentDisplayOptions(this._router.url);
        this.setDisplayOptionsSelected();
        this.initiateTableProperties();
        this.setDDls();
    }

    initiateTableProperties() {
        this.columnInfoList = [
            {
                fieldId: 'rank',
                fieldName: 'Rank',
                fieldOrder: 0,
                isSortable: true,
                defaultSorting: 'asc',
                headerAlignment: 'center',
                dataAlignment: 'center',
                dataType: 'number',
                width: 75,
                filterable: false,
                filter: 'numeric',
            },
            {
                fieldId: this.hierarchyName,
                fieldName: this.roleName,
                backUpFieldId: 'hierarchyField',
                isHyperlink: true,
                cellClickEventType: 'hyperlink',
                hyperlinkPproperties: {
                    hyperlinkUrlBase: '/performance/scorecards/',
                    hyperlinkUrlSegment: '',
                    queryParams: {},
                    queryParamsInfList: [],
                },
                fieldOrder: 1,
                isSortable: true,
                headerAlignment: 'left',
                dataAlignment: 'left',
                dataType: 'string',
                width: 160,
                filterable: false,
                filter: 'text',
            },
            {
                fieldId: 'friendlyTenure',
                fieldName: 'Tenure',
                fieldOrder: 2,
                isSortable: false,
                headerAlignment: 'right',
                dataAlignment: 'right',
                dataType: 'string',
                decimalPlaces: 0,
                width: 90,
                filterable: false,
                filter: 'numeric',
                isDynamicColumn: true,
            },
            {
                fieldId: 'wgtKPI',
                fieldName: 'placeholder',
                fieldOrder: 3,
                isSortable: true,
                headerAlignment: 'right',
                dataAlignment: 'right',
                dataType: 'number',
                decimalPlaces: 0,
                width: 90,
                filterable: false,
                filter: 'numeric',
                isDynamicColumn: true,
            },
            {
                fieldId: 'wgtTarget',
                fieldName: 'Target',
                fieldOrder: 4,
                isSortable: true,
                headerAlignment: 'right',
                dataAlignment: 'right',
                dataType: 'number',
                decimalPlaces: 2,
                width: 100,
                filterable: false,
                filter: 'numeric',
            },
            {
                fieldId: 'wgtPct',
                fieldName: '% of Target',
                fieldOrder: 5,
                isSortable: true,
                headerAlignment: 'right',
                dataAlignment: 'right',
                dataType: 'percent',
                decimalPlaces: 0,
                width: 110,
                filterable: false,
                filter: 'numeric',
            },
            {
                fieldId: 'scoreValuesSparkline',
                fieldName: 'Per Month',
                fieldOrder: 6,
                isSortable: false,
                headerAlignment: 'left',
                dataAlignment: 'left',
                dataType: 'chart',
                width: 195,
                filterable: false,
                hideIf: 'excel',
                chartProperties: {
                    chart: null,
                    dataType: 'number',
                    chartToFieldId: 'rank',
                    chartDataField: 'wgtKPI',
                    inComponentChartBuilding: false,
                    tooltipProperties: {
                        labelField: 'clientKPIMatrixDescription',
                        datatype: null,
                        precision: null,
                    },
                },
            },
            {
                fieldId: 'volumeSparkline',
                fieldName: 'Volume Trend',
                fieldOrder: 7,
                isSortable: false,
                headerAlignment: 'left',
                dataAlignment: 'left',
                dataType: 'chart',
                width: 195,
                filterable: false,
                hideIf: 'excel',
                chartProperties: {
                    chart: null,
                    dataType: 'number',
                    chartToFieldId: 'rank',
                    chartDataField: 'loanVolume',
                    inComponentChartBuilding: false,
                    tooltipProperties: {
                        labelField: 'associatedMilestone',
                        datatype: 'currency',
                        precision: 0,
                    },
                },
            },
            {
                fieldId: 'loanVolume',
                fieldName: 'Volume',
                fieldOrder: 8,
                isSortable: true,
                headerAlignment: 'right',
                dataAlignment: 'right',
                dataType: 'currency',
                decimalPlaces: 0,
                width: 120,
                filterable: false,
                filter: 'numeric',
            },
            {
                fieldId: 'unitsSparkline',
                fieldName: 'Units Trend',
                fieldOrder: 9,
                isSortable: false,
                headerAlignment: 'left',
                dataAlignment: 'left',
                dataType: 'chart',
                width: 195,
                filterable: false,
                hideIf: 'excel',
                chartProperties: {
                    chart: null,
                    dataType: 'number',
                    chartToFieldId: 'rank',
                    chartDataField: 'loanCount',
                    inComponentChartBuilding: false,
                    tooltipProperties: {
                        labelField: 'associatedMilestone',
                        datatype: 'number',
                        precision: 0,
                    },
                },
            },
            {
                fieldId: 'loanCount',
                fieldName: 'Units',
                isHyperlink: false,
                isHierarchyEntity: false,
                fieldOrder: 10,
                isSortable: true,
                headerAlignment: 'right',
                dataAlignment: 'right',
                dataType: 'number',
                decimalPlaces: 0,
                width: 80,
                filterable: false,
                filter: 'numeric',
            },
        ];

        this.customTableInfo = {
            tableId: 'performance-insights-kpi-sparkline-table',
            tableHeight: 600,
            hasData: false,
            isDataReady: false,
            columnInfoList: this.columnInfoList,
            dataSet: [],
            searchTerm: this.searchTerm,
            action: NoDataActionType.pageStart,
            title: this.pageHeadline.pageTitle,
            bandColorList: [],
            dataHandleInUiSide: true,
            showSummary: false,
        };
    }

    setSelectedProfile() {
        let defaultProfileAbbv = 'LO';
        if (!(this.selectedPosition && this.selectedPosition.length > 0)) {
            let defaultProfile = this.positions.find(
                (pos) => pos.positionAbbv === defaultProfileAbbv
            );
            if (defaultProfile) {
                this.selectedPosition = [defaultProfile];
            }
        }
        this.positionAbbv = this.selectedPosition[0].positionAbbv;
    }

    setDDls() {
        if (this.roleName && this.roleName === 'Employee') {
            this._filterService.getPosition(this.parameters).subscribe((res) => {
                this.positions = res;
                if (res && res.length > 0) {
                    this.setSelectedProfile();
                    this.getKpiList();
                }
            });
        } else {
            this.selectedPosition = [];
            this.positionAbbv = 'LO';
            this.getKpiList();
        }
    }

    getKpiList() {
        if (this.roleName.toLowerCase() === 'employee') {
            this.parameters.staffingKey = this.selectedPosition[0].staffingKey.toString();
            this.parameters.positionAbbv = this.selectedPosition[0].positionAbbv;
        } else {
            this.parameters.staffingKey = '0';
        }

        this._KPIService.getKPIScorecardSelection(this.parameters).subscribe(
            (data) => {
                if (data && data.length > 0) {
                    this.KPIs = data;
                    let selectedKpi;
                    if (this.selectedKPI && this.selectedKPI.length > 0) {
                        selectedKpi = this.KPIs.find(
                            (kpi) => kpi.kpiMatrixKey === this.selectedKPI[0].kpiMatrixKey
                        );
                    }

                    if (selectedKpi) {
                        this.selectedKPI = [selectedKpi];
                    } else {
                        this.selectedKPI = [this.KPIs[0]];
                    }

                    this.getKPIData();
                }
                ///// else??
            },
            (err) => {
                throw new Error(err);
            }
        );
    }

    setDisplayOptionsSelected(): void {
        let asOfDateId = 'scorecardAsOf';
        let syncWithDisplayOptionId = 'dateRange_Q_Y_3m_6m_12m';
        this.syncAsOfDateInf = {
            asOfDateId: asOfDateId,
            syncWithDisplayOptionId: syncWithDisplayOptionId,
        };
        let fromDatePeriod = this.displayOptions.find(
            (item) => item.optionKey.toLowerCase() === syncWithDisplayOptionId.toLowerCase()
        ).selectedOptions;
        let loanType = this.displayOptions.find(
            (item) => item.optionKey === 'loanCategory'
        ).selectedOptions;
        let loanPurpose = this.displayOptions.find(
            (item) => item.optionKey === 'loanPurpose'
        ).selectedOptions;
        let channel = this.displayOptions.find(
            (item) => item.optionKey === 'channel'
        ).selectedOptions;
        this.roleName = this.displayOptions.find(
            (item) => item.optionKey === 'groupByRoles'
        ).selectedOptions;
        let asOf = this.displayOptions.find(
            (item) => item.optionKey.toLowerCase() === asOfDateId.toLowerCase()
        ).selectedOptions;
        this.hierarchyName =
            this.roleName.toLowerCase() === 'employee' ? 'employee' : 'hierarchyName';
        this.hierarchyIdentifier =
            this.roleName.toLowerCase() === 'employee'
                ? 'employeeIdentifier'
                : 'hierarchyIdentifier';

        this.parameters = {
            fromDatePeriod: fromDatePeriod,
            kpiMatrix: '',
            loanType: loanType,
            loanPurpose: loanPurpose,
            channel: channel,
            hFilterJSON: this.hFilterJSON,
            roleName: this.roleName,
            ymc: asOf,
        };
    }

    applyNewFilters(event: string) {
        this.hFilterJSON = event;
        this.parameters.hFilterJSON = this.hFilterJSON;
        this.getKPIData();
    }

    contentChanged(event: IKPISparklineData[]) {
        this.setColumnFormat();
        this.updateTableColumnProperties();
        this.paginatedData = event;
        let chartParams: IChartParameters = {
            paginatedData: this.paginatedData,
            fullDataset: this.fullDataset,
            toGroupByColumnHeader: this.hierarchyIdentifier,
            calculateChange: true,
            columnList: this.columnInfoList,
            chartIdColumn: 'rank',
            chartType: 'area',
        };

        setTimeout(() => {
            this.spco.setCharts(chartParams);
        }, 0);
    }
    updateTableColumnProperties() {
        let hierarchyFieldInf = this.columnInfoList.find(
            (colInf) => colInf.backUpFieldId === 'hierarchyField'
        );

        if (hierarchyFieldInf) {
            hierarchyFieldInf.fieldId = this.hierarchyName;
            hierarchyFieldInf.fieldName = this.roleName;

            if (hierarchyFieldInf.hyperlinkPproperties) {
                hierarchyFieldInf.hyperlinkPproperties.queryParams = {};
                hierarchyFieldInf.hyperlinkPproperties.hyperlinkUrlSegment = this.getUrlSegment();
                hierarchyFieldInf.hyperlinkPproperties.queryParamsInfList = [
                    {
                        paramId: 'id',
                        paramDataField: this.hierarchyIdentifier,
                    },
                ];
            }

            if (this.roleName !== 'Employee') {
                hierarchyFieldInf.hyperlinkPproperties.queryParams['level'] = this.roleName;
            }
        }
    }

    setColumnFormat() {
        let columnsTobeFormatted = ['wgtKPI', 'wgtTarget'];
        columnsTobeFormatted.forEach((fieldId) => {
            let valueColumn = this.columnInfoList.find((col) => col.fieldId === fieldId);
            if (valueColumn) {
                if (valueColumn.isDynamicColumn) {
                    valueColumn.fieldName = this.selectedKPI[0].kpiDisplayShort;
                }
                let format = this.currentPeriodData[0]?.format ?? valueColumn.dataType;
                format = format === 'percentage' ? 'percent' : format;
                let precision = this.currentPeriodData[0]?.precision ?? valueColumn.decimalPlaces;

                valueColumn.dataType = format as any;
                valueColumn.decimalPlaces = precision;
            }
        });
    }

    resetTableData() {
        let customTableInfo = Object.assign({}, this.customTableInfo);
        customTableInfo.dataSet = [];
        return this._reportService.refreshCustomMatTable(
            customTableInfo,
            false,
            false,
            this.action,
            this.searchTerm
        );
    }

    getKPIData(): any {
        this.parameters.kpiMatrix =
            this.selectedKPI.length > 0 ? this.selectedKPI[0].kpiMatrixKey.toString() : '';
        this.customTableInfo = this.resetTableData();

        this._KPIService.getSparklineData(this.parameters).subscribe(
            (data) => {
                this.fullDataset = data;
                this.currentPeriodData = this.reusableFunctions.getFriendlyTenure(
                    this.spco.groupDataByColumn(data, 'isCurrentPeriod', true)
                );
                this.customTableInfo.dataSet = this.currentPeriodData;
                this.customTableInfo = this._reportService.refreshCustomMatTable(
                    this.customTableInfo,
                    true,
                    true,
                    this.action,
                    this.searchTerm
                );
            },
            (error) => {
                this.customTableInfo.dataSet = [];
                this.customTableInfo = this._reportService.refreshCustomMatTable(
                    this.customTableInfo,
                    false,
                    true,
                    NoDataActionType.error,
                    this.searchTerm
                );
            }
        );
    }

    getUrlSegment() {
        return this.selectedPosition && this.selectedPosition[0]
            ? this.selectedPosition[0].urlSegment.toLowerCase()
            : 'branch';
    }

    applyDisplayOptions(event: DisplayOptions[]) {
        this.displayOptions = event;
        this.setDisplayOptionsSelected();
        this.updateTableColumnProperties();
        this.setDDls();
    }

    cellClickedEvent(event: ICellClickEventInfo) {
        let fieldInfo = event.columnInfo;
        let dataItem = event.cellData;
        let hyperlinkPproperties = fieldInfo.hyperlinkPproperties;
        if (hyperlinkPproperties) {
            hyperlinkPproperties.queryParamsInfList.forEach((param) => {
                hyperlinkPproperties.queryParams[param.paramId] = dataItem[param.paramDataField];
            });

            this._router.navigate(
                [hyperlinkPproperties.hyperlinkUrlBase, hyperlinkPproperties.hyperlinkUrlSegment],
                { queryParams: hyperlinkPproperties.queryParams }
            );
        }
    }

    onSelectionChange(selectedKPI: KPI) {
        this.selectedKPI = [
            this.KPIs.find(
                (kpi) => kpi.clientKPIMatrixDescription === selectedKPI.clientKPIMatrixDescription
            ),
        ];
        this.getKPIData();
    }

    onSelectionChangeProfile(event) {
        this.setSelectedProfile();
        this.getKpiList();
    }

    onDeSelect(event) {
        this.selectedKPI = [event];
    }

    onDeSelectProfile(event) {
        this.selectedPosition = [event];
    }
}
